import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nanoid } from 'nanoid';
import { fetchChatbotConfig } from './api/fetchChatbotConfig';
import { delay } from './api/dummyDelay';
import {
  sendChatMessage as sendChatMessageTransport,
  sendChatbotSseTurn,
  sendChatbotWsTurn,
} from './middleware/chatTransport';
import {
  isAiChatDemoResponsesEnabled,
  isChatbotSseEnabled,
  isChatbotWebSocketEnabled,
} from './config';
import { getChatbotWsSession, refreshChatbotWs } from './ws/chatbotWsScope';
import {
  buildDemoAssistantMarkdown,
  streamDemoAssistantContent,
} from './demoAssistant';

const AiChatContext = createContext(null);

export function useAiChat() {
  const ctx = useContext(AiChatContext);
  if (!ctx) {
    throw new Error('useAiChat must be used inside AiChatProvider');
  }
  return ctx;
}

/**
 * @param {{
 *   children: React.ReactNode,
 *   modulePath: string,
 *   projectId: string,
 *   chatbotModuleId?: string | null,
 *   wsConnectError?: string | null,
 * }} props
 */
export function AiChatProvider({
  children,
  modulePath,
  projectId,
  chatbotModuleId = null,
  wsConnectError = null,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState('');
  const conversationIdRef = useRef('');
  const inFlightRef = useRef(false);
  /** Incremented on new chat so in-flight turns cannot mutate state after reset. */
  const chatGenerationRef = useRef(0);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (wsConnectError) {
      setError(wsConnectError);
    }
  }, [wsConnectError]);

  const resetChat = useCallback(() => {
    chatGenerationRef.current += 1;
    setMessages([]);
    setError(null);
    setConversationId('');
    conversationIdRef.current = '';
    setStreaming(false);
    setLoading(false);
    inFlightRef.current = false;
    if (!isAiChatDemoResponsesEnabled()) {
      refreshChatbotWs().catch(() => {});
    }
  }, []);

  /** @deprecated Use resetChat — same behavior (full thread reset). */
  const clearMessages = resetChat;

  const dismissError = useCallback(() => setError(null), []);

  const runSseTurn = useCallback(
    async (displayUserText, apiMessage, { appendUser = true } = {}) => {
      if (!isChatbotSseEnabled() && !isChatbotWebSocketEnabled()) {
        return;
      }
      if (inFlightRef.current) {
        return;
      }

      const opGen = chatGenerationRef.current;

      if (isAiChatDemoResponsesEnabled()) {
        inFlightRef.current = true;
        if (chatGenerationRef.current !== opGen) {
          inFlightRef.current = false;
          return;
        }
        setLoading(true);
        setError(null);

        if (appendUser && displayUserText) {
          setMessages((prev) => [
            ...prev,
            { id: nanoid(), role: 'user', content: displayUserText },
          ]);
        }

        const assistantMsgId = nanoid();
        setMessages((prev) => [
          ...prev,
          {
            id: assistantMsgId,
            role: 'assistant',
            content: '',
            streaming: true,
          },
        ]);
        setLoading(false);
        setStreaming(true);

        const echoSource = (apiMessage || displayUserText || '').trim();
        const fullText = buildDemoAssistantMarkdown(echoSource);

        try {
          if (conversationIdRef.current === '') {
            if (chatGenerationRef.current === opGen) {
              conversationIdRef.current = 'demo-local';
              setConversationId('demo-local');
            }
          }
          await streamDemoAssistantContent((chunk) => {
            if (chatGenerationRef.current !== opGen) {
              return;
            }
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: m.content + chunk }
                  : m,
              ),
            );
          }, fullText);
          if (chatGenerationRef.current === opGen) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, streaming: false } : m,
              ),
            );
          }
        } catch (e) {
          if (chatGenerationRef.current === opGen) {
            setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
            const msg = e?.message || 'Something went wrong. Please try again.';
            setError(typeof msg === 'string' ? msg : String(msg));
          }
        } finally {
          if (chatGenerationRef.current === opGen) {
            setStreaming(false);
            setLoading(false);
          }
          if (chatGenerationRef.current === opGen) {
            // eslint-disable-next-line require-atomic-updates -- in-flight mutex ref after async demo stream
            inFlightRef.current = false;
          }
        }
        return;
      }

      inFlightRef.current = true;
      if (chatGenerationRef.current !== opGen) {
        inFlightRef.current = false;
        return;
      }

      if (appendUser && displayUserText) {
        setMessages((prev) => [
          ...prev,
          { id: nanoid(), role: 'user', content: displayUserText },
        ]);
      }

      if (!chatbotModuleId) {
        if (chatGenerationRef.current === opGen) {
          // eslint-disable-next-line require-atomic-updates -- release mutex before early return
          inFlightRef.current = false;
          setError(
            'AI chat needs a module configuration. Use a project module that has chatbot settings.',
          );
        }
        return;
      }

      setLoading(true);
      setError(null);

      let config;
      try {
        config = await fetchChatbotConfig(chatbotModuleId);
      } catch (e) {
        if (chatGenerationRef.current === opGen) {
          setLoading(false);
          // eslint-disable-next-line require-atomic-updates -- in-flight mutex ref in error path
          inFlightRef.current = false;
          setError(e?.message || 'Could not load chatbot configuration.');
        }
        return;
      }

      if (chatGenerationRef.current !== opGen) {
        return;
      }

      if (!config) {
        if (chatGenerationRef.current === opGen) {
          setLoading(false);
          // eslint-disable-next-line require-atomic-updates -- in-flight mutex ref in error path
          inFlightRef.current = false;
          setError('Could not load chatbot configuration for this module.');
        }
        return;
      }

      const assistantMsgId = nanoid();
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          streaming: true,
        },
      ]);
      setLoading(false);
      setStreaming(true);

      const streamParams = {
        message: apiMessage,
        conversationId: conversationIdRef.current,
        settings: config,
        onTextDelta: (chunk) => {
          if (chatGenerationRef.current !== opGen) {
            return;
          }
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId
                ? { ...m, content: m.content + chunk }
                : m,
            ),
          );
        },
        onConversationId: (id) => {
          if (chatGenerationRef.current !== opGen) {
            return;
          }
          conversationIdRef.current = id;
          setConversationId(id);
        },
      };

      try {
        if (isChatbotWebSocketEnabled()) {
          await sendChatbotWsTurn(getChatbotWsSession(), streamParams);
        } else {
          await sendChatbotSseTurn(streamParams);
        }
        if (chatGenerationRef.current === opGen) {
          setMessages((prev) => {
            const next = prev.map((m) =>
              m.id === assistantMsgId ? { ...m, streaming: false } : m,
            );
            const row = next.find((m) => m.id === assistantMsgId);
            if (row && !(row.content || '').trim()) {
              return next.filter((m) => m.id !== assistantMsgId);
            }
            return next;
          });
        }
      } catch (e) {
        if (chatGenerationRef.current === opGen) {
          setMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            'Something went wrong. Please try again.';
          setError(typeof msg === 'string' ? msg : String(msg));
        }
      } finally {
        if (chatGenerationRef.current === opGen) {
          setStreaming(false);
          setLoading(false);
          // eslint-disable-next-line require-atomic-updates -- in-flight mutex ref after async SSE turn
          inFlightRef.current = false;
        }
      }
    },
    [chatbotModuleId],
  );

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) {
        return;
      }

      if (isChatbotWebSocketEnabled() || isChatbotSseEnabled()) {
        await runSseTurn(trimmed, trimmed, { appendUser: true });
        return;
      }

      const opGen = chatGenerationRef.current;

      if (isAiChatDemoResponsesEnabled()) {
        if (chatGenerationRef.current !== opGen) {
          return;
        }
        setError(null);
        const userMsg = {
          id: nanoid(),
          role: 'user',
          content: trimmed,
        };
        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);
        try {
          await delay(
            Number(process.env.REACT_APP_AI_CHAT_DUMMY_REPLY_LATENCY_MS) || 400,
          );
          if (chatGenerationRef.current !== opGen) {
            return;
          }
          setMessages((prev) => [
            ...prev,
            {
              id: nanoid(),
              role: 'assistant',
              content: buildDemoAssistantMarkdown(trimmed),
            },
          ]);
        } catch (e) {
          if (chatGenerationRef.current === opGen) {
            const msg =
              e?.response?.data?.message ||
              e?.message ||
              'Something went wrong. Please try again.';
            setError(typeof msg === 'string' ? msg : String(msg));
          }
        } finally {
          if (chatGenerationRef.current === opGen) {
            setLoading(false);
          }
        }
        return;
      }

      if (chatGenerationRef.current !== opGen) {
        return;
      }
      setError(null);
      const userMsg = {
        id: nanoid(),
        role: 'user',
        content: trimmed,
      };
      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);
      try {
        const nextThread = [...messages, userMsg].map(({ role, content }) => ({
          role,
          content,
        }));
        const replyText = await sendChatMessageTransport({
          projectId,
          messages: nextThread,
          modulePath: modulePath || '',
        });
        if (chatGenerationRef.current !== opGen) {
          return;
        }
        setMessages((prev) => [
          ...prev,
          {
            id: nanoid(),
            role: 'assistant',
            content: replyText,
          },
        ]);
      } catch (e) {
        if (chatGenerationRef.current === opGen) {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            'Something went wrong. Please try again.';
          setError(typeof msg === 'string' ? msg : String(msg));
        }
      } finally {
        if (chatGenerationRef.current === opGen) {
          setLoading(false);
        }
      }
    },
    [messages, modulePath, projectId, runSseTurn],
  );

  const value = useMemo(
    () => ({
      messages,
      loading,
      streaming,
      error,
      sendMessage,
      resetChat,
      clearMessages,
      dismissError,
    }),
    [
      messages,
      loading,
      streaming,
      error,
      sendMessage,
      resetChat,
      clearMessages,
      dismissError,
    ],
  );

  return (
    <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
  );
}
